import { useEffect } from "react";
import SectionLink from "@/components/powlo/SectionLink";
import { consumeQueuedSection } from "@/components/powlo/sectionNav";
import PostcodeOrBillFork from "@/components/powlo/home/PostcodeOrBillFork";
import RotatingWord from "@/components/powlo/home/RotatingWord";
import ExamplePlanCard from "@/components/powlo/home/ExamplePlanCard";
import CtaBanner from "@/components/powlo/home/CtaBanner";
import FaqAccordion from "@/components/powlo/home/FaqAccordion";
import PowloLayout from "@/components/powlo/PowloLayout";
import Seo from "@/components/Seo";
import { useFinanceConfig } from "@/api/hooks";
import { FINANCE_FAQ } from "@/data/faqContent";
import { gbp } from "@/utils/phoenixFinanceExample";
import { TRUSTPILOT_PROFILE_URL, TRUSTPILOT_REVIEW } from "@/constants/trustpilot";

const HOME_FAQS = [
  {
    q: "Do you really not need to visit?",
    a: "Most homes are designed fully remotely, and a certified expert approves every design before anything is ordered. A technical survey only happens if your property needs one or you ask for one — and it's usually done from your portal with photos, a short video walk-around and a few questions, or on a video call with us.",
  },
  {
    q: "How can AI design my system?",
    a: "It combines satellite and aerial imagery, EPC and property records, local irradiance history and live tariff data with your own bill or usage. Hundreds of data points are modelled together to find the setup with the fastest payback for your specific roof.",
  },
  {
    q: "Who actually installs it?",
    a: "A vetted local firm from the Powlo installer network. Powlo designs the system, prices it, and stands behind it — the installer fits it. You get one point of contact throughout: us.",
  },
  {
    q: "I live in a listed home or conservation area. Can I still get solar?",
    a: "Often, yes. Our AI checks planning constraints for your address and flags any steps you'd need to take. Where panels genuinely aren't workable, we'll say so and suggest a battery-only setup instead.",
  },
  {
    q: FINANCE_FAQ.question,
    a: FINANCE_FAQ.answer,
  },
];

const HERO_CASH_PRICE = 6749;

const DATA_POINTS = [
  { title: "Roof shape & pitch", sub: "Satellite and aerial imagery" },
  { title: "Orientation & shading", sub: "Trees, chimneys, neighbours" },
  { title: "Your bill & usage", sub: "Real consumption, not averages" },
  { title: "Live tariffs", sub: "Octopus Flux, Go and more" },
  { title: "Local sunshine", sub: "Irradiance for your postcode" },
  { title: "EPC & property data", sub: "Age, type, insulation" },
  { title: "Planning checks", sub: "Listed homes, conservation areas" },
  { title: "Your future plans", sub: "EV, heat pump, growing family" },
];

const STEPS = [
  { n: "01", tag: "60 seconds", title: "Postcode or bill", body: "Tell us where you live, or upload a bill for an exact design." },
  { n: "02", tag: "No site visit", title: "AI designs it remotely", body: "Satellite roof mapping, your usage and live tariffs, modelled in seconds." },
  { n: "03", tag: "AI powered, human backed", title: "An expert signs it off", body: "Tweak it until it's right, then a certified specialist validates and approves the design." },
  { n: "04", tag: "One point of contact", title: "Installed and handled", body: "Fitted by the Powlo installer network. We handle paperwork and aftercare." },
];

const COMPARISON = [
  { label: "Getting a quote", usual: "A salesperson sits in your living room", powlo: "Designed by AI, approved by an expert" },
  { label: "Speed", usual: "Days or weeks", powlo: "A full design in seconds" },
  { label: "Pressure", usual: '"Sign today" discounts', powlo: "Your plan, your timeline" },
  { label: "Tariffs", usual: "Rarely considered", powlo: "Modelled against Flux, Go and more" },
  { label: "The numbers", usual: "A rough guess", powlo: "Your roof, your usage, your forecast" },
  { label: "After install", usual: "You're on your own", powlo: "Paperwork and aftercare handled" },
];

export default function Home() {
  useEffect(() => {
    consumeQueuedSection();
  }, []);

  return (
    <PowloLayout>
      <Seo
        title="Powlo — Turning data into decisions"
        description="Powlo's AI reads your roof, your energy use and your tariff, then designs the solar and battery system that pays back fastest."
        path="/"
      />
      <section className="flex flex-col items-center gap-5 px-5 pb-10 pt-14 text-center sm:px-8 sm:pt-20 lg:px-20">
        <h1 className="m-0 max-w-5xl font-display text-5xl font-bold leading-[1.05] tracking-tight sm:text-7xl lg:text-8xl">
        <span className="text-[#7E7D86]">Your roof could be </span><span className="highlight-lime">paying you.</span>
        </h1>
        <p className="m-0 max-w-2xl text-base leading-relaxed text-muted sm:text-lg">
          Powlo&apos;s AI reads your roof, usage and tariff, then designs the system that&apos;s{" "}
          <span className="whitespace-nowrap">right for you.</span>
          <br />
          No site visit. Expert approved.
        </p>
        <div id="check" className="w-full max-w-3xl scroll-mt-24">
          <PostcodeOrBillFork />
        </div>
        <div className="-mt-2 max-w-2xl text-base leading-relaxed text-[#2A2A33]">
          Your Energy Plan is free. Solar + battery systems <br />
          from <strong>{gbp(HERO_CASH_PRICE)}</strong> fully installed. <PhoenixFinanceApplyLink />
        </div>
        <div className="flex flex-wrap items-center justify-center gap-6 pt-3 text-base text-muted">
          <TrustpilotBadge className="h-10 w-auto sm:h-12" />
          <span aria-hidden="true" className="hidden h-8 w-px bg-border sm:block" />
          <span>
            <strong className="text-ink">2,178</strong> systems designed
          </span>
        </div>
      </section>

      <section id="example" className="flex scroll-mt-36 flex-col gap-10 px-5 pb-24 sm:px-8 lg:px-20">
        <div className="flex flex-col items-start justify-between gap-8 lg:flex-row lg:items-end">
          <div className="flex flex-col gap-4">
            <div className="text-sm font-bold tracking-widest text-indigo">TRY AN EXAMPLE ENERGY PLAN</div>
            <h2 className="m-0 font-display text-4xl font-bold leading-[1.05] tracking-tight sm:text-6xl">
              Powlo recommends.
              <br />
              You decide.
            </h2>
          </div>
          <p className="m-0 max-w-lg text-lg leading-relaxed text-muted">
            Every plan starts with our AI&apos;s best setup for your home. Then it&apos;s yours to customise: more
            or fewer panels, a bigger battery, your future plans. Everything recalculates instantly.
          </p>
        </div>
        <ExamplePlanCard />
      </section>

      <section className="flex flex-col gap-10 px-5 pb-24 sm:px-8 lg:px-20">
        <div className="flex flex-col items-start justify-between gap-8 lg:flex-row lg:items-end">
          <div className="flex flex-col gap-4">
            <div className="text-sm font-bold tracking-widest text-indigo">TURNING DATA INTO DECISIONS</div>
            <h2 className="m-0 font-display text-4xl font-bold leading-[1.05] tracking-tight sm:text-6xl">
              Designed by Powlo AI.
              <br />
              In seconds.
            </h2>
          </div>
          <p className="m-0 max-w-lg text-lg leading-relaxed text-muted">
            Behind that plan: your property, how you actually use energy, and every live tariff — modelled
            together, then optimised for the fastest payback. Checked and approved by a certified expert
            before anything is ordered.
          </p>
        </div>
        <div className="flex flex-col gap-6 rounded-[40px] bg-navy p-6 text-white sm:p-11">
          <div className="flex flex-col gap-4">
            <div className="font-bold">Hundreds of data points, including:</div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {DATA_POINTS.map((d) => (
                <div key={d.title} className="flex items-start gap-3 rounded-2xl bg-navyLight p-4">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-lime" />
                  <span>
                    <strong className="block text-sm">{d.title}</strong>
                    <span className="text-xs text-[#C9D1DE]">{d.sub}</span>
                  </span>
                </div>
              ))}
            </div>
          </div>
          <div className="flex items-start gap-3 rounded-2xl border border-navyBorder bg-navyLight px-5 py-4 font-bold">
            <span className="mt-0.5 shrink-0 text-lime">★</span>
            <span className="min-w-0">The result: a complete, costed design in seconds — expert-approved, and free.</span>
          </div>
        </div>
      </section>

      <section id="how" className="flex scroll-mt-36 flex-col gap-10 px-5 pb-24 sm:px-8 lg:px-20">
        <div className="flex flex-col gap-4">
          <div className="text-sm font-bold tracking-widest text-indigo">HOW IT WORKS</div>
          <h2 className="m-0 font-display text-4xl font-bold leading-[1.05] tracking-tight sm:text-6xl">
            From postcode to powered-up.
          </h2>
        </div>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {STEPS.map((s) => (
            <div key={s.n} className="flex flex-col gap-4 rounded-[28px] border border-border bg-white p-6 sm:p-8">
              <div className="flex items-center justify-between">
                <span
                  className="flex items-center justify-center rounded-2xl bg-navy font-display text-lg font-bold text-lime"
                  style={{ width: 52, height: 52 }}
                >
                  {s.n}
                </span>
                <span className="rounded-full bg-[#F1F0EC] px-3 py-1.5 text-xs font-bold">{s.tag}</span>
              </div>
              <h3 className="m-0 font-display text-2xl font-bold tracking-tight">{s.title}</h3>
              <p className="m-0 text-base leading-relaxed text-muted">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="flex flex-col items-center gap-5 px-5 pb-20 text-center sm:px-8 lg:px-20">
        <h2 className="m-0 font-display text-4xl font-bold leading-tight tracking-tight sm:text-6xl">
          <span className="text-[#7E7D86]">Built for </span>
          <RotatingWord />
          {/* <span className="text-[#7E7D86]">.</span> */}
        </h2>
        <p className="m-0 max-w-2xl text-lg leading-relaxed text-muted">
          Flat roofs, listed homes, conservation areas, shaded or north-facing roofs. Powlo&apos;s AI checks
          what&apos;s possible for yours, flags any planning steps, and suggests a battery-only setup when
          panels don&apos;t make sense.
        </p>
      </section>

      <section className="flex flex-col gap-10 px-5 pb-24 sm:px-8 lg:px-20">
        <h2 className="m-0 font-display text-3xl font-bold tracking-tight sm:text-5xl">
          Every home can save something.
        </h2>
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <PathCard
            title="Solar + battery"
            body="Generate your own power, store it, and sell the spare back at peak rates."
            cta="Design my system →"
          />
          <PathCard
            title="Battery only"
            body="Shaded, north-facing or a listed roof? Charge on a cheap overnight rate and run your home on it all day."
            cta="See if it works for me →"
            badge="No panels needed"
            dark
          />
          <PathCard
            title="EV + home energy"
            body="Charging at home? We design everything around cheap overnight rates, so the car and the house both win."
            cta="Plan around my EV →"
          />
        </div>
      </section>

      <section id="compare" className="flex scroll-mt-20 flex-col gap-10 px-5 pb-24 sm:px-8 lg:px-20">
        <h2 className="m-0 font-display text-3xl font-bold tracking-tight sm:text-5xl">Powlo vs the usual way</h2>
        <div className="rounded-[28px] border border-border bg-white md:hidden">
          <div className="flex flex-wrap gap-x-5 gap-y-2 px-5 pb-1 pt-5 text-[11px] font-bold tracking-widest">
            <span className="flex items-center gap-2 text-mutedLight">
              <XDot /> THE USUAL WAY
            </span>
            <span className="flex items-center gap-2 text-indigo">
              <CheckDot /> POWLO
            </span>
          </div>
          {COMPARISON.map((row) => (
            <div key={row.label} className="border-t border-[#ECEAE3] px-5 py-4">
              <div className="font-bold">{row.label}</div>
              <div className="mt-3 flex items-start gap-3 text-sm leading-relaxed text-mutedLight">
                <XDot />
                <span className="min-w-0">{row.usual}</span>
              </div>
              <div className="mt-3 flex items-start gap-3 text-sm font-bold leading-relaxed">
                <CheckDot />
                <span className="min-w-0">{row.powlo}</span>
              </div>
            </div>
          ))}
        </div>
        <div className="hidden overflow-x-auto rounded-[28px] border border-border bg-white px-4 sm:px-8 md:block">
          <div className="grid min-w-[640px] grid-cols-[180px_1fr_1fr] gap-6 py-5 text-xs font-bold tracking-widest text-mutedLight sm:grid-cols-[220px_1fr_1fr]">
            <div />
            <div>THE USUAL WAY</div>
            <div className="text-indigo">POWLO</div>
          </div>
          {COMPARISON.map((row) => (
            <div key={row.label} className="grid min-w-[640px] grid-cols-[180px_1fr_1fr] items-center gap-6 border-t border-[#ECEAE3] py-5 text-base sm:grid-cols-[220px_1fr_1fr] sm:text-lg">
              <div className="font-bold">{row.label}</div>
              <div className="flex items-center gap-3 text-mutedLight">
                <XDot /> {row.usual}
              </div>
              <div className="flex items-center gap-3 font-bold">
                <CheckDot /> {row.powlo}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section id="faq" className="grid scroll-mt-20 grid-cols-1 gap-10 px-5 pb-24 sm:px-8 lg:grid-cols-[1.25fr_1fr] lg:px-20">
        <div className="flex flex-col gap-3">
          <h2 className="m-0 mb-2 font-display text-3xl font-bold tracking-tight sm:text-5xl">Good questions</h2>
          <FaqAccordion items={HOME_FAQS} />
        </div>
        {/* <HomeownerReview /> */}
      </section>

      <CtaBanner
        heading={
          <>
            Your roof&apos;s been doing nothing for years. <span className="text-lime">Let&apos;s fix that.</span>
          </>
        }
        body="Free plan, no site visit, no pressure."
      />
    </PowloLayout>
  );
}

function PhoenixFinanceApplyLink() {
  const { data: financeConfig } = useFinanceConfig();
  const applyUrl = financeConfig?.applyUrl?.trim() ?? "";

  if (applyUrl === "") return null;

  return (
    <a
      href={applyUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="text-indigo underline"
    >
      Finance available through Phoenix
    </a>
  );
}

function TrustpilotBadge({ className }: { className?: string }) {
  const image = (
    <img src="/trustpilot.png" alt="Rated on Trustpilot" width={640} height={313} className={className} />
  );
  if (!TRUSTPILOT_PROFILE_URL) return image;
  return (
    <a href={TRUSTPILOT_PROFILE_URL} target="_blank" rel="noreferrer" className="inline-flex">
      {image}
    </a>
  );
}

function HomeownerReview() {
  return (
    <div className="flex flex-col gap-6 rounded-[28px] bg-navy p-8 text-white sm:p-10">
      <div className="text-sm font-bold tracking-widest text-lime">WHAT HOMEOWNERS SAY</div>
      {TRUSTPILOT_REVIEW ? (
        <>
          <div className="flex-1 font-display text-2xl font-bold leading-snug tracking-tight">
            &ldquo;{TRUSTPILOT_REVIEW.quote}&rdquo;
          </div>
          <div className="text-base text-[#C9D1DE]">
            <strong className="text-white">{TRUSTPILOT_REVIEW.author}</strong>
            {TRUSTPILOT_REVIEW.detail ? ` · ${TRUSTPILOT_REVIEW.detail}` : null}
          </div>
        </>
      ) : (
        <TrustpilotBadge className="h-12 w-auto self-start" />
      )}
    </div>
  );
}

function PathCard({
  title,
  body,
  cta,
  badge,
  dark,
}: {
  title: string;
  body: string;
  cta: string;
  badge?: string;
  dark?: boolean;
}) {
  return (
    <div
      className={`flex flex-col gap-4 rounded-[28px] p-8 ${
        dark ? "bg-indigo text-white" : "border border-border bg-white"
      }`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <h3 className="m-0 font-display text-3xl font-bold tracking-tight">{title}</h3>
        {badge && (
          <span className="shrink-0 rounded-full bg-lime px-3.5 py-2 text-xs font-bold text-ink">{badge}</span>
        )}
      </div>
      <p className={`m-0 flex-1 text-lg leading-relaxed ${dark ? "text-[#E6E5FF]" : "text-muted"}`}>{body}</p>
      <SectionLink href="/#check" className={`self-start font-bold no-underline ${dark ? "text-lime" : "text-indigo"}`}>
        {cta}
      </SectionLink>
    </div>
  );
}

function CheckDot() {
  return (
    <span className="flex shrink-0 items-center justify-center rounded-full bg-lime" style={{ width: 26, height: 26 }}>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0C0D10" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 6 9 17l-5-5" />
      </svg>
    </span>
  );
}

function XDot() {
  return (
    <span className="flex shrink-0 items-center justify-center rounded-full bg-[#ECEAE3]" style={{ width: 26, height: 26 }}>
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#8A8D95" strokeWidth="3.4" strokeLinecap="round">
        <path d="M6 12h12" />
      </svg>
    </span>
  );
}
